import { ApolloClient, ApolloLink } from '@apollo/client';
import { useGoogleLogin } from '@react-oauth/google';
import { baseClientOption } from 'apollo/client';
import { createAuthenticationLink, errorLink, mainLink } from 'apollo/link';
import { useAuthenticationContext } from 'components/providers/authentication-provider';
import {
  NEW_GOOGLE_SIGN_IN,
  NewGoogleSignInData,
  NewGoogleSignInVariables,
  SIGN_IN_WITH_SOCIAL_ACCOUNT,
  SignInWithSocialAccountData,
  SignInWithSocialAccountVariables,
} from 'graphql/main/mutations';
import { USER, UserData } from 'graphql/main/queries';
import { useFlexMutation, useToast } from 'hooks';
import { useRouter } from 'next/router';
import { useLogin } from 'react-facebook';

const ThirdPartyAuthentication = () => {
  const router = useRouter();
  const { query, push } = router;
  const { redirect } = query;
  const { login } = useLogin();
  const { setCurrentUser, saveAtmnhaAuthentication, saveECommerceAuthentication } =
    useAuthenticationContext();
  const [signInWithSocialAccount] = useFlexMutation<
    SignInWithSocialAccountData,
    SignInWithSocialAccountVariables
  >('main', SIGN_IN_WITH_SOCIAL_ACCOUNT);
  const [newGoogleSignIn] = useFlexMutation<NewGoogleSignInData, NewGoogleSignInVariables>(
    'main',
    NEW_GOOGLE_SIGN_IN,
  );
  const { addToast } = useToast();

  const signInWithFacebook = async () => {
    const loginResponse = await login({});
    const { authResponse } = loginResponse;
    const { accessToken } = authResponse;
    const { data: signInWithSocialAccountData, errors: signInWithSocialAccountErrors } =
      await signInWithSocialAccount({
        variables: {
          type: 'facebook',
          idToken: accessToken,
        },
        onError: (error) => {
          addToast('error', error.message);
        },
      });
    if (signInWithSocialAccountErrors) {
      saveAtmnhaAuthentication?.(undefined);
      saveECommerceAuthentication?.(undefined);
      addToast('error', signInWithSocialAccountErrors?.[0]?.message);
    } else {
      const { signInWithSocialAccount: _signInWithSocialAccount } =
        signInWithSocialAccountData ?? {};
      const { payload } = _signInWithSocialAccount ?? {};
      const { accessToken } = payload ?? {};
      if (accessToken) {
        saveAtmnhaAuthentication?.(payload);
        const authLink = createAuthenticationLink(accessToken);
        const mainClient = new ApolloClient({
          ...baseClientOption,
          link: ApolloLink.from([errorLink, authLink, mainLink]),
        });
        const { data: userData, error: userError } = await mainClient.query<UserData>({
          query: USER,
        });
        const { user } = userData;
        if (userError || !user) {
          saveAtmnhaAuthentication?.(undefined);
          saveECommerceAuthentication?.(undefined);
          addToast('error', userError ? userError.message : 'Lỗi xác thực!');
        } else {
          const { ecommerceData } = user ?? {};
          saveECommerceAuthentication?.(ecommerceData);
          setCurrentUser?.(user);
          addToast('success', 'Đăng nhập thành công');
          push(redirect ? `${redirect}` : '/');
        }
      }
    }
  };
  const signInWithGoogle = useGoogleLogin({
    onSuccess: async (authResponse) => {
      const { access_token } = authResponse;
      const { data: newGoogleSignInData, errors: newGoogleSignInErrors } = await newGoogleSignIn({
        variables: {
          accessToken: access_token,
        },
      });
      if (newGoogleSignInErrors) {
        saveAtmnhaAuthentication?.(undefined);
        saveECommerceAuthentication?.(undefined);
        addToast('error', newGoogleSignInErrors?.[0]?.message);
      } else {
        const { newGoogleSignIn: _newGoogleSignIn } = newGoogleSignInData ?? {};
        const { payload } = _newGoogleSignIn ?? {};
        const { accessToken } = payload ?? {};
        if (accessToken) {
          saveAtmnhaAuthentication?.(payload);
          const authLink = createAuthenticationLink(accessToken);
          const mainClient = new ApolloClient({
            ...baseClientOption,
            link: ApolloLink.from([errorLink, authLink, mainLink]),
          });
          const { data: userData, error: userError } = await mainClient.query<UserData>({
            query: USER,
          });
          if (userError) {
            saveAtmnhaAuthentication?.(undefined);
            saveECommerceAuthentication?.(undefined);
            addToast('error', userError.message);
          } else {
            const { user } = userData;
            const { ecommerceData } = user ?? {};
            saveECommerceAuthentication?.(ecommerceData);
            setCurrentUser?.(user);
            addToast('success', 'Đăng nhập thành công');
            push(redirect ? `${redirect}` : '/');
          }
        }
      }
    },
  });

  return (
    <div id='third-party-authentication' className='flex flex-row justify-center space-x-[24px]'>
      <button
        type='button'
        className='flex h-[40px] w-[150px] items-center justify-center space-x-[4px] rounded-[6px] bg-background'
        onClick={signInWithFacebook}
      >
        <div className='min-h-[24px] min-w-[24px] bg-[url("/icons/facebook3.svg")] bg-contain bg-center bg-no-repeat' />
        <span className='text-[12px] leading-[15px]'>Facebook</span>
      </button>
      <button
        type='button'
        className='flex h-[40px] w-[150px] items-center justify-center space-x-[8px] rounded-[6px] bg-background'
        onClick={() => {
          signInWithGoogle();
        }}
      >
        <div className='min-h-[24px] min-w-[24px] bg-[url("/icons/google.svg")] bg-contain bg-center bg-no-repeat' />
        <span className='text-[12px] leading-[15px]'>Google</span>
      </button>
    </div>
  );
};

export default ThirdPartyAuthentication;
